import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ImageOff, Trash2 } from 'lucide-react';
import { api } from '../../api';
import { Modal } from '../../componentes/Modal';
import { distanciaTexto } from '../agenda/comum';

export interface Anexo {
  id: string; tipo: string; bytes: string; capturado_em: string; upload_em: string | null;
  ativo_id: string | null; ativo: string | null; usuario_id: string | null; usuario: string | null; distancia_local_m: string | null;
  referencia?: string | null; distancia_referencia_m?: string | null;
  origem?: 'camera' | 'galeria'; original_em?: string | null;
}

const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
const url = (id: string) => `/api/anexos/${id}/arquivo`;

/**
 * Fotos da OS. A imagem vem da API com o cookie de sessão (não há link público).
 * Miniatura = a própria foto (~1600 px, poucas centenas de KB) com carregamento preguiçoso; sem miniatura gerada no servidor por ora.
 */
export function Fotos({ anexos, raio, podeApagar, aoMudar }: { anexos: Anexo[]; raio: number; podeApagar: boolean; aoMudar: () => void }) {
  const fotos = anexos.filter((a) => a.tipo === 'foto');
  const [aberta, setAberta] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [apagando, setApagando] = useState(false);
  const atual = aberta == null ? null : fotos[aberta];

  useEffect(() => {
    if (aberta == null) return;
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setAberta((i) => (i == null ? i : Math.min(fotos.length - 1, i + 1)));
      if (e.key === 'ArrowLeft') setAberta((i) => (i == null ? i : Math.max(0, i - 1)));
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [aberta, fotos.length]);

  async function apagar() {
    if (!atual || !window.confirm('Apagar esta foto? Não dá para desfazer.')) return;
    setApagando(true); setErro(null);
    try { await api(`/anexos/${atual.id}`, { metodo: 'DELETE' }); setAberta(null); aoMudar(); }
    catch (e) { setErro((e as Error).message); }
    finally { setApagando(false); }
  }

  return (
    <section className="secao">
      <h2>Fotos <span className="contador">{fotos.length}</span></h2>
      {fotos.length === 0 ? <p className="nota">Nenhuma foto. O técnico tira pelo app durante o atendimento.</p> : (
        <ul className="grade-fotos">
          {fotos.map((f, i) => (
            <li key={f.id}>
              <button type="button" onClick={() => setAberta(i)} aria-label={`Abrir foto ${i + 1}${f.ativo ? ` (${f.ativo})` : ''}`}>
                <img src={url(f.id)} alt="" loading="lazy" decoding="async"
                  onError={(e) => { (e.currentTarget.parentElement as HTMLElement).classList.add('foto-falhou'); }} />
                <ImageOff className="icone-falha" size={22} aria-hidden />
              </button>
              <small>{dataHora(f.capturado_em)}{f.ativo ? ` · ${f.ativo}` : ''}{f.origem === 'galeria' ? ' · da galeria' : ''}</small>
            </li>
          ))}
        </ul>
      )}

      <Modal titulo={atual ? `Foto ${(aberta ?? 0) + 1} de ${fotos.length}` : 'Foto'} aberto={atual != null} aoFechar={() => { setAberta(null); setErro(null); }} classe="modal-foto">
        {atual && (
          <div className="visor-foto">
            <div className="visor-img">
              <img src={url(atual.id)} alt={`Foto ${(aberta ?? 0) + 1} da OS`} />
              {aberta! > 0 && <button type="button" className="visor-nav ant" onClick={() => setAberta(aberta! - 1)} aria-label="Foto anterior"><ChevronLeft size={26} /></button>}
              {aberta! < fotos.length - 1 && <button type="button" className="visor-nav prox" onClick={() => setAberta(aberta! + 1)} aria-label="Próxima foto"><ChevronRight size={26} /></button>}
            </div>
            <div className="visor-info">
              <span>{dataHora(atual.capturado_em)}{atual.usuario ? ` · ${atual.usuario}` : ''}</span>
              {atual.ativo && <span>Equipamento: <strong>{atual.ativo}</strong></span>}
              {atual.origem === 'galeria' && <span className="aviso-galeria">Da galeria do celular{atual.original_em ? ` — foto de ${dataHora(atual.original_em)}` : ' — data original desconhecida'}; sem localização.</span>}
              {atual.distancia_local_m != null && (
                Number(atual.distancia_local_m) <= raio
                  ? <span className="geo-ok">no local</span>
                  : <small title="A foto não guarda a precisão do GPS; distância só indicativa">tirada {distanciaTexto(atual.distancia_local_m)}</small>
              )}
              {atual.distancia_referencia_m != null && atual.referencia && (
                <small title="Distância à coordenada do setor/via/caixa do equipamento; a foto não guarda a precisão do GPS">
                  a {Math.round(Number(atual.distancia_referencia_m))} m de <strong>{atual.referencia}</strong></small>
              )}
              {atual.upload_em && Math.abs(Date.parse(atual.upload_em) - Date.parse(atual.capturado_em)) > 10 * 60_000 && <small>enviada às {dataHora(atual.upload_em)}</small>}
              <a href={url(atual.id)} target="_blank" rel="noopener">Abrir original</a>
              {podeApagar && <button type="button" className="btn-fantasma perigo" onClick={apagar} disabled={apagando}><Trash2 size={16} aria-hidden /> {apagando ? 'Apagando…' : 'Apagar'}</button>}
            </div>
            {erro && <p className="alerta" role="alert">{erro}</p>}
          </div>
        )}
      </Modal>
    </section>
  );
}
